import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, toggleIn, listOf } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Clock, Battery, MapPin, Play, Pause, Check, SkipForward, RotateCcw } from "lucide-react";

const TIMES = [5, 10, 20, 30, 60];
const ENERGY = ["low", "medium", "high"];
const PLACES = ["home", "out", "computer", "phone"];

const Choice = ({ label, icon: Icon, options, value, onChange, testidPrefix, render, multi }) => (
  <div className="mb-7">
    <p className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3 inline-flex items-center gap-2">
      <Icon className="w-4 h-4" /> {label}
    </p>
    <div className="flex flex-wrap gap-2.5">
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`${testidPrefix}-${opt}`}
          onClick={() => onChange(opt)}
          className={`px-5 py-4 rounded-2xl text-base font-semibold capitalize transition-all active:scale-95 ${
            (multi ? value.includes(opt) : value === opt)
              ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
          }`}
        >
          {render ? render(opt) : opt}
        </button>
      ))}
    </div>
  </div>
);

export default function NextRightThing() {
  const [time, setTime] = useState(20);
  const [energy, setEnergy] = useState("medium");
  const [places, setPlaces] = useState([]);
  const [task, setTask] = useState(null);
  const [reason, setReason] = useState("");
  const [smallerAction, setSmallerAction] = useState(null);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);

  // timer
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(time * 60);
  const [useEstimate, setUseEstimate] = useState(false);
  const intervalRef = useRef(null);

  const find = async () => {
    setBusy(true);
    try {
      const res = await api.nextRightThing({ time_minutes: time, energy, locations: places });
      setTask(res.task);
      setReason(res.reason || "");
      setSmallerAction(res.smaller_action || null);
      setSearched(true);
      setSeconds(time * 60);
      setUseEstimate(false);
      setRunning(false);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            toast("Time's up. Wrap it up or keep going.");
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  const done = async () => {
    await api.updateTask(task.id, { status: "done" });
    // If this task IS today's trust deposit (same wording), keep that promise too.
    try {
      const today = await api.getToday();
      const dep = today.trust_deposit;
      if (dep && !dep.completed &&
          dep.promise.trim().toLowerCase() === task.title.trim().toLowerCase()) {
        await api.completeTrustDeposit(dep.id);
        toast.success("Done — and that was today's trust deposit. Trust deposited.");
      } else {
        toast.success("Done. That's the next right thing handled.");
      }
    } catch (e) {
      toast.success("Done. That's the next right thing handled.");
    }
    reset();
  };
  const skip = async () => {
    await api.updateTask(task.id, { status: "skipped" });
    toast("Skipped — no guilt. Let's find another.");
    setRunning(false);
    find();
  };
  const reset = () => {
    setTask(null);
    setReason("");
    setSmallerAction(null);
    setSearched(false);
    setRunning(false);
    setPlaces([]);
  };

  const applyEstimate = () => {
    setSeconds(task.estimated_minutes * 60);
    setUseEstimate(true);
    setRunning(false);
  };

  if (task) {
    return (
      <PageShell title="Just this one">
        <div className="nrt-fade">
          <div className="bg-zinc-100 rounded-3xl p-6 shadow-md mb-6" data-testid="recommended-task">
            <p className="text-xs font-semibold text-[#365458] uppercase tracking-wide mb-2">Your next right thing</p>
            <h2 className="font-heading text-2xl font-extrabold text-zinc-900 leading-tight">{task.title}</h2>
            <div className="flex flex-wrap gap-3 mt-3 text-sm text-zinc-500">
              <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />~{task.estimated_minutes}m</span>
              <span className="inline-flex items-center gap-1 capitalize"><MapPin className="w-4 h-4" />{listOf(task, "locations", "location").join(" · ")}</span>
              {listOf(task, "categories", "category").map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600">{c}</span>
              ))}
            </div>
            {reason && (
              <p className="text-sm text-zinc-600 mt-3 pt-3 border-t border-zinc-200" data-testid="recommendation-reason">
                {reason}
              </p>
            )}
            {smallerAction && (
              <div className="mt-3 rounded-xl bg-[#365458]/10 px-3 py-2" data-testid="smaller-action">
                <p className="text-sm text-[#2A4346] font-medium">Too big? {smallerAction}</p>
              </div>
            )}
          </div>

          {/* Timer */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl py-8 text-center mb-5">
            <p className="font-heading text-7xl font-black tracking-tighter text-zinc-50 tabular-nums" data-testid="timer-display">
              {mmss}
            </p>
            <p className="text-zinc-500 text-sm mt-1">
              {useEstimate ? `Estimate block (${task.estimated_minutes}m)` : `Your ${time}m block`}
            </p>
          </div>

          {task.estimated_minutes < time && !useEstimate && (
            <button
              data-testid="use-estimate-button"
              onClick={applyEstimate}
              className="w-full mb-4 text-sm text-[#7FB0B5] bg-zinc-800/60 rounded-xl py-3 hover:bg-zinc-800"
            >
              This only takes ~{task.estimated_minutes}m — use the shorter timer instead
            </button>
          )}

          <div className="grid grid-cols-2 gap-3 mb-3">
            {!running ? (
              <Button data-testid="timer-start-button" onClick={() => setRunning(true)}
                className="h-16 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-lg font-bold col-span-1">
                <Play className="w-5 h-5 mr-2" /> Start
              </Button>
            ) : (
              <Button data-testid="timer-pause-button" onClick={() => setRunning(false)}
                className="h-16 rounded-2xl bg-zinc-700 hover:bg-zinc-600 text-white text-lg font-bold col-span-1">
                <Pause className="w-5 h-5 mr-2" /> Pause
              </Button>
            )}
            <Button data-testid="task-done-button" onClick={done}
              className="h-16 rounded-2xl bg-zinc-100 hover:bg-white text-zinc-900 text-lg font-bold col-span-1">
              <Check className="w-5 h-5 mr-2" /> Done
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="task-skip-button" onClick={skip} variant="outline"
              className="h-12 rounded-2xl bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">
              <SkipForward className="w-4 h-4 mr-2" /> Skip
            </Button>
            <Button data-testid="next-reset-button" onClick={reset} variant="outline"
              className="h-12 rounded-2xl bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">
              <RotateCcw className="w-4 h-4 mr-2" /> Change
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Next right thing" subtitle="Answer three quick things. Get one task.">
      <Choice label="How much time?" icon={Clock} options={TIMES} value={time}
        onChange={setTime} testidPrefix="time-choice" render={(t) => (t === 60 ? "60+" : t)} />
      <Choice label="Energy level?" icon={Battery} options={ENERGY} value={energy}
        onChange={setEnergy} testidPrefix="energy-choice" />
      <Choice label="Where are you?" icon={MapPin} options={PLACES} value={places} multi
        onChange={(v) => setPlaces(toggleIn(places, v, true))} testidPrefix="place-choice" />

      <Button
        data-testid="find-task-button"
        onClick={find}
        disabled={busy}
        className="w-full h-16 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-lg font-bold"
      >
        {busy ? "Finding…" : "Tell me what to do"}
      </Button>

      {searched && !task && (
        <div className="bg-zinc-800/60 rounded-2xl p-6 text-center mt-5" data-testid="no-task-message">
          <p className="text-zinc-300 font-medium">Nothing fits that combo right now.</p>
          <p className="text-zinc-500 text-sm mt-1">Try more time or different energy — or take the win and rest.</p>
        </div>
      )}
    </PageShell>
  );
}
